import React from 'react';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { noop } from '@lux/helpers';
import useTranslate from '../../hooks/useTranslation';
import useThirdPartyIndicator from '../../hooks/useThirdPartyIndicator';
import { TOMO_INDICATORS } from '../../constants/third-party';
import SelectionModal from '../SelectionModal';
import {
  gaSendEvent,
  dataLayerPush,
  isPromotionPlan
} from '../../helpers/common';
import { getProductDetailSubmitDataLayer } from '../../helpers/dataLayerHelpers';
import { useSelector } from 'react-redux';
import { BaseState } from '../../types/state.types';
import { FlowType } from '../../types/dataLayer.type';

export const TransactionDialog = (props: PropTypes) => {
  const { t } = useTranslate();
  const { hasIndicator: hasTomoIndicator } = useThirdPartyIndicator(
    TOMO_INDICATORS
  );

  const { accessory, plan, product, promotions } = useSelector<
    BaseState,
    Partial<BaseState>
  >(state => state);

  const { open, onClose } = props;
  const isPromoPlan =
    promotions?.promotionLoaded &&
    plan?.selectedPlan?.planId &&
    promotions?.promotionsPlansList?.some(
      p => p.planId === plan?.selectedPlan.planId
    );

  const pushEcommerceDataLayer = (flow: FlowType) => {
    dataLayerPush(
      getProductDetailSubmitDataLayer({
        selectedPlan: plan?.selectedPlan,
        selectedVariant: product?.selectedProduct,
        accessories: accessory?.selectedAccessories,
        flow,
        location: window.location
      }),
      true
    );
  };

  const handleRecontractClick = () => {
    gaSendEvent({
      eventCategory: 'Mobile/phone-detail/Recontract',
      eventAction: 'Button',
      eventLabel: 'Recontract - Clicked on recontract option'
    });

    pushEcommerceDataLayer(FlowType.RECON);

    dataLayerPush({
      event: 'Button Click - Transaction Modal',
      transaction: 'recontract'
    });

    navigate('/choose-your-number');
  };

  const handleNewNumberClick = () => {
    gaSendEvent({
      eventCategory: 'Mobile/phone-detail/New',
      eventAction: 'Button',
      eventLabel: 'New - Clicked on new number option'
    });

    pushEcommerceDataLayer(FlowType.NEW);
    dataLayerPush({
      event: 'Button Click - Transaction Modal',
      transaction: 'new'
    });

    navigate('/number-selection#new-number');
  };

  const handlePortinClick = () => {
    gaSendEvent({
      eventCategory: 'Mobile/phone-detail/Port-in',
      eventAction: 'Button',
      eventLabel: 'Port-in - Clicked on port-in option'
    });

    pushEcommerceDataLayer(FlowType.PORT_IN);

    dataLayerPush({
      event: 'Button Click - Transaction Modal',
      transaction: 'port-in'
    });

    navigate('/number-selection#port-in');
  };

  const TRANSACTION_OPTION_BUTTONS = {
    R: {
      buttonText: t('BUTTON_RECONTRACT'),
      onButtonClick: handleRecontractClick
    },
    N: {
      buttonText: t('BUTTON_NEW_NUMBER'),
      onButtonClick: handleNewNumberClick
    },
    P: {
      buttonText: t('BUTTON_TRANSFER_EXISTING_NUMBER'),
      onButtonClick: handlePortinClick
    }
  };

  const buttons = isPromotionPlan(promotions, plan?.selectedPlan)
    ? (promotions?.transactionOptions || [])
        .map(opt => TRANSACTION_OPTION_BUTTONS[opt])
        .filter(Boolean)
    : [
        ...(!hasTomoIndicator
          ? [
              {
                buttonText: t('BUTTON_RECONTRACT'),
                onButtonClick: handleRecontractClick
              }
            ]
          : []),
        {
          buttonText: t('BUTTON_NEW_NUMBER'),
          onButtonClick: handleNewNumberClick
        },
        {
          buttonText: t('BUTTON_TRANSFER_EXISTING_NUMBER'),
          onButtonClick: handlePortinClick
        }
      ];

  const data = {
    rhs: undefined,
    lhs: {
      title: t('TRANSACTION_OPTION_TITLE'),
      buttons: buttons
    }
  };

  return <SelectionModal open={open} onClose={onClose} data={data} />;
};

type PropTypes = {
  open: boolean;
  isGomoToSingtelEnabled: boolean;
  onClose: () => void;
  onClickGomoPortIn?: () => void;
};

TransactionDialog.defaultProps = {
  onClose: noop,
  onClickGomoPortIn: noop
};

export default TransactionDialog;
